import { Shield, TrendingUp, Coins, Layers, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// FIN-4 — ClasseAgregadaCard.
// Card de uma classe de ativo. Cor + ícone + glow contextual (gain
// emerald, loss coral). Empty state quando classe vazia.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

const CLASS_META = {
  "Renda Fixa": {
    icon: Shield,
    iconBg: "bg-brand-green/15",
    iconColor: "text-brand-green",
    accentColor: "text-brand-green",
  },
  "Renda Variável": {
    icon: TrendingUp,
    iconBg: "bg-brand-cyan/15",
    iconColor: "text-brand-cyan",
    accentColor: "text-brand-cyan",
  },
  "Cripto": {
    icon: Coins,
    iconBg: "bg-status-warning/15",
    iconColor: "text-status-warning",
    accentColor: "text-status-warning",
  },
  "Outros": {
    icon: Layers,
    iconBg: "bg-foreground-muted/15",
    iconColor: "text-foreground-muted",
    accentColor: "text-foreground",
  },
};

export default function ClasseAgregadaCard({
  classLabel,           // "Renda Fixa" | "Renda Variável" | "Cripto" | "Outros"
  data,                 // { current, gain_loss, gain_loss_pct, pct_of_portfolio } — null se vazio
}) {
  const meta = CLASS_META[classLabel] || CLASS_META["Outros"];
  const Icon = meta.icon;
  const isEmpty = !data || !Number(data.current);

  if (isEmpty) {
    return (
      <Card
        variant="elevated"
        className="glass-card glow-hover p-5 md:p-6 min-h-[180px] flex flex-col items-center justify-center text-center opacity-60"
        data-testid={`invest-class-${classLabel.toLowerCase().replace(/\s/g, "-")}-empty`}
      >
        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-3", meta.iconBg)}>
          <Icon className={cn("w-5 h-5", meta.iconColor)} aria-hidden="true" />
        </div>
        <p className="text-caption text-foreground-muted uppercase mb-1" style={{ letterSpacing: "0.10em" }}>
          {classLabel}
        </p>
        <p className="text-micro text-foreground-subtle">Sem ativos nesta classe</p>
      </Card>
    );
  }

  const gain = Number(data.gain_loss || 0);
  const gainPct = Number(data.gain_loss_pct || 0);
  const pctPortfolio = Number(data.pct_of_portfolio || 0);

  const gainSign = gain > 0 ? "+" : (gain < 0 ? "−" : "");
  const gainAbs = Math.abs(gain);
  const GainIcon = gain > 0 ? ArrowUpRight : (gain < 0 ? ArrowDownRight : Minus);
  const gainTone = gain > 0
    ? "text-brand-green bg-brand-green/10"
    : (gain < 0 ? "text-status-error bg-status-error/10" : "text-foreground-muted bg-surface-card-hover");

  // Glow contextual sutil — usa arbitrary values pra não poluir tokens
  const glowClass = gain > 0
    ? "shadow-[0_0_18px_rgba(0,255,163,0.25)]"
    : gain < 0
      ? "shadow-[0_0_18px_rgba(255,77,109,0.25)]"
      : "";

  return (
    <Card
      variant="elevated"
      className={cn("glass-card glow-hover p-5 md:p-6 min-h-[180px] flex flex-col", glowClass)}
      data-testid={`invest-class-${classLabel.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", meta.iconBg)}>
          <Icon className={cn("w-4.5 h-4.5", meta.iconColor)} aria-hidden="true" />
        </div>
        <span
          className="text-caption text-foreground-muted uppercase font-semibold"
          style={{ letterSpacing: "0.10em" }}
        >
          {classLabel}
        </span>
      </div>

      <div
        className={cn(
          "font-display font-bold leading-[0.95] tracking-[-0.02em] tabular-nums truncate mb-2",
          "text-[28px] md:text-[36px] lg:text-[40px]",
          meta.accentColor,
        )}
      >
        {fmtBRL(data.current)}
      </div>

      <div className={cn(
        "self-start inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-caption font-medium mb-2",
        gainTone,
      )}>
        <GainIcon className="w-3 h-3" aria-hidden="true" />
        {gainSign}{fmtBRL(gainAbs)} · {gainSign}{Math.abs(gainPct).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%
      </div>

      <p className="text-micro text-foreground-muted mt-auto">
        {pctPortfolio.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% da carteira
      </p>
    </Card>
  );
}
